import { useEffect, useMemo, useState } from "react";
import { CheckCircle2, Circle, Clock, Search, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import AppShell from "../components/AppShell";
import Logo from "../components/Logo";
import { api } from "../lib/api";

const STATUS_META = {
  completed: { label: "Completada", color: "#238a65", bg: "#dcf7ed", Icon: CheckCircle2 },
  in_progress: { label: "En progreso", color: "#a97a11", bg: "#fff2c9", Icon: Clock },
  not_started: { label: "Empieza aquí", color: "#4169e1", bg: "#eef4ff", Icon: Circle },
};

export default function AcademiaPage() {
  const [dashboard, setDashboard] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [track, setTrack] = useState(() => localStorage.getItem("academia_track") || "impuestos");
  const [tracks, setTracks] = useState([]);
  const [category, setCategory] = useState("");
  const [search, setSearch] = useState("");
  const [glossaryQ, setGlossaryQ] = useState("");
  const [glossary, setGlossary] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => { localStorage.setItem("academia_track", track); setCategory(""); }, [track]);

  const loadDashboard = () =>
    api.get(`/academia/dashboard?track=${track}`, { withCredentials: true })
      .then((r) => setDashboard(r.data))
      .catch((e) => setError(e.response?.data?.detail || "No pudimos cargar la Academia."));

  const loadLessons = () => {
    const params = new URLSearchParams();
    params.set("track", track);
    if (category) params.set("category", category);
    if (search) params.set("search", search);
    api.get(`/academia/lessons?${params.toString()}`, { withCredentials: true })
      .then((r) => setLessons(r.data.lessons || []))
      .catch((e) => setError(e.response?.data?.detail || "No pudimos cargar las lecciones."));
  };

  const loadGlossary = () => {
    const q = glossaryQ ? `?search=${encodeURIComponent(glossaryQ)}` : "";
    api.get(`/academia/glossary${q}`, { withCredentials: true })
      .then((r) => setGlossary(r.data || []))
      .catch(() => setGlossary([]));
  };

  useEffect(() => {
    loadDashboard();
    api.get("/academia/tracks", { withCredentials: true }).then((r) => setTracks(r.data || [])).catch(() => setTracks([]));
  }, [track]);
  useEffect(() => { loadLessons(); }, [track, category, search]);
  useEffect(() => { const t = setTimeout(loadGlossary, 150); return () => clearTimeout(t); }, [glossaryQ]);

  const totals = dashboard?.totals || { total: 0, completed: 0, in_progress: 0, pct: 0 };
  const categories = useMemo(() => dashboard?.categories || [], [dashboard]);

  return (
    <AppShell>
      <main className="page academia-page" data-testid="academia-page">
        <header className="expenses-head">
          <div>
            <div className="academia-eyebrow-row">
              <Logo variant="mark" size="sm" testid="academia-brand-mark" />
              <small className="eyebrow">MI CONTADOR · ACADEMIA</small>
            </div>
            <h1>Aprende impuestos, <em>sin miedo.</em></h1>
            <p className="academia-subtitle">Lecciones cortitas, en español simple, como si te lo contara un amigo.</p>
            {tracks.length > 1 && (
              <div className="academia-track-tabs" data-testid="academia-track-tabs">
                {tracks.map((t) => (
                  <button
                    key={t.id}
                    className={track === t.id ? "active" : ""}
                    onClick={() => setTrack(t.id)}
                    data-testid={`academia-track-${t.id}`}
                  >
                    <span>{t.emoji}</span>
                    <div>
                      <b>{t.name}</b>
                      <small>{t.tagline}</small>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="academia-progress-hero" data-testid="academia-progress-hero">
            <div className="academia-progress-ring" style={{ background: `conic-gradient(#238a65 ${totals.pct * 3.6}deg, #eef2f7 0deg)` }}>
              <div><b>{totals.pct}%</b><small>completado</small></div>
            </div>
            <ul>
              <li><b>{totals.completed}</b> ✅</li>
              <li><b>{totals.in_progress}</b> ⏳</li>
              <li><b>{totals.total}</b> 📚</li>
            </ul>
          </div>
        </header>

        {error && <p className="expenses-error" data-testid="academia-error">{error}</p>}

        {dashboard?.suggested_next && (
          <Link to={`/academia/${dashboard.suggested_next.slug}`} className="academia-suggested" data-testid="academia-suggested">
            <Sparkles size={22} />
            <div>
              <small>Sigue por aquí</small>
              <b>{dashboard.suggested_next.emoji} {dashboard.suggested_next.title}</b>
              <em>{dashboard.suggested_next.tagline}</em>
            </div>
            <span>{dashboard.suggested_next.minutes} min</span>
          </Link>
        )}

        <section className="academia-filters" data-testid="academia-filters">
          <div className="academia-search">
            <Search size={16} />
            <input
              type="text"
              placeholder="Buscar lecciones (ej: IVA, factura, e.firma)..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="academia-search-input"
            />
          </div>
          <div className="academia-chips" data-testid="academia-category-chips">
            <button className={!category ? "active" : ""} onClick={() => setCategory("")} data-testid="academia-chip-all">Todas</button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                className={category === cat.id ? "active" : ""}
                onClick={() => setCategory(category === cat.id ? "" : cat.id)}
                data-testid={`academia-chip-${cat.id}`}
                style={category === cat.id ? { background: cat.color, color: "#fff", borderColor: cat.color } : {}}
              >
                {cat.name} <span className="chip-count">{cat.completed}/{cat.total}</span>
              </button>
            ))}
          </div>
        </section>

        <section className="academia-grid" data-testid="academia-lessons-grid">
          {lessons.length === 0 && (
            <p className="academia-empty" data-testid="academia-empty">No hay lecciones que coincidan con tu búsqueda.</p>
          )}
          {lessons.map((lesson) => {
            const meta = STATUS_META[lesson.status] || STATUS_META.not_started;
            const Icon = meta.Icon;
            return (
              <Link
                key={lesson.slug}
                to={`/academia/${lesson.slug}`}
                className="academia-card"
                data-testid={`academia-lesson-${lesson.slug}`}
                style={{ borderTop: `4px solid ${lesson.category_color}` }}
              >
                <div className="academia-card-head">
                  <span className="academia-card-emoji">{lesson.emoji}</span>
                  <span className="academia-card-status" style={{ background: meta.bg, color: meta.color }}>
                    <Icon size={12} /> {meta.label}
                  </span>
                </div>
                <b>{lesson.title}</b>
                <p>{lesson.tagline}</p>
                <div className="academia-card-foot">
                  <span style={{ color: lesson.category_color }}>{lesson.category_name}</span>
                  <em>{lesson.minutes} min</em>
                </div>
              </Link>
            );
          })}
        </section>

        {track === "impuestos" && (
          <>
            <section className="academia-glossary-section" data-testid="academia-glossary-section">
              <div>
                <h2>📖 Glosario rápido</h2>
                <p>Toca cualquier término para leer su explicación simple.</p>
              </div>
              <div className="academia-search" style={{ maxWidth: 320 }}>
                <Search size={16} />
                <input
                  type="text"
                  placeholder="Buscar en glosario..."
                  value={glossaryQ}
                  onChange={(e) => setGlossaryQ(e.target.value)}
                  data-testid="academia-glossary-search"
                />
              </div>
            </section>
            <section className="academia-glossary-grid" data-testid="academia-glossary-grid">
              {glossary.map((item) => (
                <article key={item.term} className="academia-glossary-item" data-testid={`glossary-${item.term.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>
                  <b>{item.term}</b>
                  <small>{item.short}</small>
                </article>
              ))}
              {glossary.length === 0 && <p className="academia-empty">Sin resultados en el glosario.</p>}
            </section>
          </>
        )}

        {dashboard?.recent && dashboard.recent.length > 0 && (
          <section className="academia-recent" data-testid="academia-recent">
            <h2>Recientes</h2>
            <div className="academia-recent-row">
              {dashboard.recent.map((r) => (
                <Link key={r.slug} to={`/academia/${r.slug}`} className="academia-recent-chip" data-testid={`academia-recent-${r.slug}`}>
                  <span>{r.emoji}</span>
                  <div>
                    <b>{r.title}</b>
                    <small>{STATUS_META[r.status]?.label}</small>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
    </AppShell>
  );
}
